import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
    CANONICAL_ORIGIN,
    DEFAULT_OG_IMAGE,
    STORE_NAME,
    canonicalFor,
} from "@/lib/seo";

/**
 * Imperatively manages <title>, <meta>, <link rel=canonical> and JSON-LD
 * blocks per route. Uses native DOM APIs (no external dependency) — zero
 * bundle weight, zero risk to the existing render tree.
 *
 * Props:
 *   title         — page title (the suffix is added automatically when needed)
 *   description   — meta description and og/twitter description
 *   path          — override path for canonical (defaults to current location)
 *   image         — absolute URL for og:image / twitter:image
 *   noindex       — adds <meta name="robots" content="noindex,follow">
 *   ogType        — defaults to "website"; product pages pass "product"
 *   jsonLd        — single object OR array of objects for <script type="application/ld+json">
 */
export default function SEO({
    title,
    description,
    path,
    image,
    noindex = false,
    ogType = "website",
    jsonLd,
}) {
    const location = useLocation();

    useEffect(() => {
        const finalPath = path ?? location.pathname + (location.search || "");
        const canonicalUrl = canonicalFor(finalPath);
        const finalTitle = title || STORE_NAME;
        const finalDesc = description || "";
        const finalImage = image || DEFAULT_OG_IMAGE;

        // ---- <title> ----
        if (document.title !== finalTitle) document.title = finalTitle;

        // helper: upsert a <meta> tag by attribute name & value
        const upsertMeta = (selector, attrs) => {
            let el = document.head.querySelector(selector);
            if (!el) {
                el = document.createElement("meta");
                Object.entries(attrs.create).forEach(([k, v]) => el.setAttribute(k, v));
                document.head.appendChild(el);
            }
            Object.entries(attrs.set).forEach(([k, v]) => el.setAttribute(k, v));
        };

        upsertMeta('meta[name="description"]', {
            create: { name: "description" },
            set: { content: finalDesc },
        });
        upsertMeta('meta[name="robots"]', {
            create: { name: "robots" },
            set: { content: noindex ? "noindex,follow" : "index,follow" },
        });

        // Open Graph
        upsertMeta('meta[property="og:title"]', {
            create: { property: "og:title" },
            set: { content: finalTitle },
        });
        upsertMeta('meta[property="og:description"]', {
            create: { property: "og:description" },
            set: { content: finalDesc },
        });
        upsertMeta('meta[property="og:url"]', {
            create: { property: "og:url" },
            set: { content: canonicalUrl },
        });
        upsertMeta('meta[property="og:image"]', {
            create: { property: "og:image" },
            set: { content: finalImage },
        });
        upsertMeta('meta[property="og:type"]', {
            create: { property: "og:type" },
            set: { content: ogType },
        });
        upsertMeta('meta[property="og:site_name"]', {
            create: { property: "og:site_name" },
            set: { content: STORE_NAME },
        });

        // Twitter
        upsertMeta('meta[name="twitter:card"]', {
            create: { name: "twitter:card" },
            set: { content: "summary_large_image" },
        });
        upsertMeta('meta[name="twitter:title"]', {
            create: { name: "twitter:title" },
            set: { content: finalTitle },
        });
        upsertMeta('meta[name="twitter:description"]', {
            create: { name: "twitter:description" },
            set: { content: finalDesc },
        });
        upsertMeta('meta[name="twitter:image"]', {
            create: { name: "twitter:image" },
            set: { content: finalImage },
        });

        // Canonical link
        let canonical = document.head.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement("link");
            canonical.setAttribute("rel", "canonical");
            document.head.appendChild(canonical);
        }
        canonical.setAttribute("href", canonicalUrl);

        // ---- JSON-LD (managed under a single id we own; replace on each route) ----
        const TAG_ID = "ost-jsonld-managed";
        document.querySelectorAll(`script[data-managed-id="${TAG_ID}"]`).forEach((s) => s.remove());
        if (jsonLd) {
            const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
            blocks.forEach((b) => {
                if (!b) return;
                const s = document.createElement("script");
                s.type = "application/ld+json";
                s.setAttribute("data-managed-id", TAG_ID);
                s.textContent = JSON.stringify(b);
                document.head.appendChild(s);
            });
        }

        // We intentionally do NOT remove these tags on unmount — the next
        // route's <SEO> will upsert them. This avoids a "no meta" flash on
        // route change.
    }, [title, description, path, image, noindex, ogType, jsonLd, location.pathname, location.search]);

    return null;
}

export { CANONICAL_ORIGIN };
